import { gql, NetworkStatus, type TypedDocumentNode } from '@apollo/client'
import { QueryRef, useMutation, useReadQuery } from '@apollo/client/react'
import { ErrorAlert } from '@repo/shared-client/components/blocks'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import SFPageLoading from '@web/src/components/primatives/sf-page-loading/sf-page-loading'
import PublicationForm from '@web/src/features/publication/publication-form/publication-form'
import { queryAndPreload } from '@web/src/lib/apollo/query-and-preload'
import { env } from '@web/src/lib/env.client'
import i18n from '@web/src/lib/intl/i18n'
import { useTranslation } from '@web/src/lib/intl/react'
import { robotsMetaForEnvironment, seo } from '@web/src/lib/seo'
import { useState } from 'react'
import toast from 'react-hot-toast'
import type {
  CreatePublicationInput,
  CreatePublicationMutation,
  CreatePublicationMutationVariables,
  CreatePublicationQuery,
} from 'types/graphql'

const CREATE_PUBLICATION_QUERY: TypedDocumentNode<CreatePublicationQuery> = gql`
  query CreatePublicationQuery {
    publicationTags {
      id
      name
    }
  }
`

const CREATE_PUBLICATION_MUTATION: TypedDocumentNode<CreatePublicationMutation, CreatePublicationMutationVariables> =
  gql`
    mutation CreatePublicationMutation($input: CreatePublicationInput!) {
      createPublication(input: $input) {
        id
        slug
      }
    }
  `

export const Route = createFileRoute('/(auth)/(main-layout)/publication/create')({
  component: CreatePublicationPage,
  head: () => {
    const t = i18n.t.bind(i18n)
    return seo({
      title: `${t('Create Publication')} | ${env.VITE_APP_NAME}`,
      description: t('Create a new Publication entry'),
      robots: robotsMetaForEnvironment({ all: 'noindex,nofollow' }),
      meta: [{ name: 'rating', content: 'adult' }],
    })
  },
  loader: async ({ context }) => {
    const { queryRef } = await queryAndPreload(context, CREATE_PUBLICATION_QUERY)
    return { queryRef }
  },
})

function CreatePublicationPage() {
  const { t } = useTranslation()
  const [showSpinner, setShowSpinnerValue] = useState(false)
  const navigate = useNavigate()
  const { queryRef } = Route.useLoaderData()
  const { data, error, networkStatus } = useReadQuery(queryRef as QueryRef<CreatePublicationQuery>)
  const isInitialLoading = networkStatus === NetworkStatus.loading

  const [createPublication] = useMutation(CREATE_PUBLICATION_MUTATION, {
    onCompleted: (result) => {
      toast.success(t('Publication created'))
      navigate({
        to: '/publication/$publicationIdOrSlug',
        params: {
          publicationIdOrSlug: result.createPublication.slug ?? result.createPublication.id,
        } as never,
      })
      setShowSpinnerValue(false)
    },
    onError: (mutationError) => {
      toast.error(mutationError.message)
      setShowSpinnerValue(false)
    },
  })

  const onSave = (input: CreatePublicationInput) => {
    setShowSpinnerValue(true)
    createPublication({ variables: { input } })
  }

  if (isInitialLoading) {
    return <SFPageLoading />
  }

  if (error) {
    return <ErrorAlert error={error} />
  }

  if (!data?.publicationTags) {
    return <ErrorAlert error={new Error(t('Publication tags not found'))} />
  }

  return (
    <div className="mx-auto w-full max-w-2xl pt-4">
      <div className="flex w-full flex-col items-stretch gap-4">
        <h1 className="w-full text-center font-bold text-4xl">{t('Create Publication')}</h1>
        <PublicationForm onSave={onSave} publicationTags={data.publicationTags} showSpinner={showSpinner} />
      </div>
    </div>
  )
}
